import type { StageTabId, StageTabRole } from "./stagingModel";

// Keyboard staging from the Explorer (TTTL-186). A leaf's only pointer
// interaction is the drag onto the open tab; Enter or Space on a focused
// leaf does the same thing the drop would, so the tree is usable without
// a mouse. Which drop that is follows from the row's kind and the tab.

export type LeafStageTarget =
  | { kind: "resource"; role: StageTabRole }
  | { kind: "schema"; role: StageTabRole }
  | { kind: "test" };

/** Where Enter on a leaf sends it, or null when the open tab takes no
 *  such row (eligibility beyond the kind — the staging rules, the locks,
 *  the view — is the drag hook's, and the caller checks it first). */
export function explorerLeafStageTarget(
  leaf: { isSchema: boolean; isTest: boolean },
  tab: StageTabId,
): LeafStageTarget | null {
  if (tab === "tests") return leaf.isTest ? { kind: "test" } : null;
  if (leaf.isTest) return null;
  if (leaf.isSchema) return { kind: "schema", role: tab };
  return { kind: "resource", role: tab };
}

/** The props a leaf row renders with: every leaf is a tab stop, and one
 *  with somewhere to land is a button that Enter or Space stages. Click is
 *  deliberately not wired — the pointer gesture stays the drag. */
export function leafActivationProps(action: (() => void) | null) {
  if (!action) return { tabIndex: 0 };
  return {
    role: "button" as const,
    tabIndex: 0,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      action();
    },
  };
}
